// File: src/pages/stylist/AppointmentsPage.tsx
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { isBefore } from 'date-fns';
import {
  getAppointmentBucket,
  getAppointmentEndTime,
  getSortDirection,
  APPOINTMENT_BUCKETS,
} from '../../utils/getAppointmentBucket';
import { useAppDispatch, useAppSelector } from '../../hooks/useRedux';
import { fetchAppointments } from '../../store/slices/appointmentsSlice';
import { useDocumentVisibleRefetch } from '../../hooks/useDocumentVisibleRefetch';
import { formatCurrency, formatAppointmentDateTime } from '../../utils/formatters';
import { Appointment, CheckInEligibility } from '../../types/appointment';
import { publicAppointmentRef } from '../../utils/publicRefs';
import {
  CalendarIcon,
  TimeIcon,
  PersonIcon,
  RefreshIcon,
  AlertIcon,
  EmailIcon
} from '../../components/common/Icons';
import { getConversationsWithUnreadCounts, getConversations, getConversation } from '../../services/chatService';
import appointmentService from '../../services/appointmentService';
import DiscountBadge from '../../components/common/DiscountBadge';

// Placeholder avatar for missing client photos
const avatarPlaceholder = 'https://via.placeholder.com/40?text=%F0%9F%91%A4';
const unknownClientName = 'Unknown Client';


interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...rest }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`appointments-tabpanel-${index}`}
    aria-labelledby={`appointments-tab-${index}`}
    className={`appointments__tab-panel ${value === index ? 'appointments__tab-panel--active' : ''}`}
    {...rest}
  >
    {children}
  </div>
);

const StylistAppointmentsPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { appointments, isLoading, error } = useAppSelector(s => s.appointments);
  const { user } = useAppSelector(s => s.auth);

  const [tabValue, setTabValue] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [refreshing, setRefreshing] = useState<boolean>(false);
  const [unreadCounts, setUnreadCounts] = useState<Record<string, number>>({});
  // Check-in modal (stylist): when PIN required or verifying
  const [checkInModalOpen, setCheckInModalOpen] = useState<boolean>(false);
  const [checkInAppointmentId, setCheckInAppointmentId] = useState<string | null>(null);
  const [checkInEligibility, setCheckInEligibility] = useState<CheckInEligibility | null>(null);
  const [checkInPinInput, setCheckInPinInput] = useState<string>('');
  const [checkInLoading, setCheckInLoading] = useState<boolean>(false);

  // Fetch unread message counts for appointments (same logic as mobile, optimized)
  const fetchUnreadCounts = useCallback(async () => {
    const userId = user?.id || (user as any)?._id;
    if (!userId) return;
    
    try {
      // Get all conversations with unread counts (optimized backend endpoint)
      const conversations = await getConversationsWithUnreadCounts();
      // Filter to only "appointment" type conversations
      const appointmentConvos = conversations.filter(
        (c) => c.type === 'appointment' && c.appointment
      );

      const newCounts: Record<string, number> = {};

      // Map unread counts to appointment IDs
      for (const convo of appointmentConvos) {
        if (convo.appointment && convo.unreadCount !== undefined) {
          newCounts[convo.appointment] = convo.unreadCount;
        }
      }

      setUnreadCounts(newCounts);
    } catch (err) {
      console.error('Error fetching unread message counts:', err);
      // Fallback to the old method if the optimized endpoint fails (matching mobile)
      try {
        const conversations = await getConversations();
        const appointmentConvos = conversations.filter(
          (c) => c.type === 'appointment' && c.appointment
        );

        const newCounts: Record<string, number> = {};
        for (const convo of appointmentConvos) {
          const convoDetails = await getConversation(convo._id);
          let count = 0;
          for (const msg of convoDetails.messages) {
            if (!msg.readBy.includes(userId) && msg.sender !== userId) {
              count++;
            }
          }
          if (convo.appointment) {
            newCounts[convo.appointment] = count;
          }
        }
        setUnreadCounts(newCounts);
      } catch (fallbackErr) {
        console.error('Error in fallback unread count fetch:', fallbackErr);
      }
    }
  }, [user]);

  // Fetch on mount
  useEffect(() => {
    dispatch(fetchAppointments());
    fetchUnreadCounts();
  }, [dispatch, fetchUnreadCounts]);

  useDocumentVisibleRefetch(() => {
    dispatch(fetchAppointments({ silent: true }));
  });

  // Pull-to-refresh
  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    await dispatch(fetchAppointments());
    await fetchUnreadCounts();
    setRefreshing(false);
  }, [dispatch, fetchUnreadCounts]);

  // Actions — use check-in API (and PIN flow when required)
  const handleCheckIn = useCallback(async (id: string) => {
    if (!window.confirm('Check in this client?')) return;
    setCheckInLoading(true);
    try {
      const eligibility = await appointmentService.getCheckInEligibility(id);
      if (!eligibility.allowed) {
        if (eligibility.requiresPinForLate || eligibility.reason === 'TOO_LATE') {
          setCheckInAppointmentId(id);
          setCheckInEligibility(eligibility);
          setCheckInPinInput('');
          setCheckInModalOpen(true);
          return;
        }
        const msg = eligibility.reason === 'TOO_EARLY_BLOCKED'
          ? 'Check-in is blocked. You can check in up to 60 minutes before the appointment.'
          : eligibility.reason === 'ALREADY_CHECKED_IN'
            ? 'This appointment is already checked in.'
            : 'Check-in is not allowed for this appointment.';
        alert(msg);
        return;
      }
      if (eligibility.requiresPin) {
        setCheckInAppointmentId(id);
        setCheckInEligibility(eligibility);
        setCheckInPinInput('');
        setCheckInModalOpen(true);
        return;
      }
      await appointmentService.checkIn(id, { method: 'stylist' });
      await dispatch(fetchAppointments());
      alert('Client checked in successfully.');
    } catch (err: any) {
      const data = err?.response?.data;
      const code = data?.code;
      const message = data?.message || err?.message || 'Check-in failed.';
      if (code === 'PIN_REQUIRED' || code === 'TOO_LATE') {
        setCheckInAppointmentId(id);
        setCheckInEligibility(await appointmentService.getCheckInEligibility(id).catch(() => null));
        setCheckInPinInput('');
        setCheckInModalOpen(true);
        return;
      }
      alert(message);
    } finally {
      setCheckInLoading(false);
    }
  }, [dispatch]);

  const handleRequestCheckInPin = useCallback(async () => {
    if (!checkInAppointmentId) return;
    setCheckInLoading(true);
    try {
      await appointmentService.requestCheckInPin(checkInAppointmentId);
      const eligibility = await appointmentService.getCheckInEligibility(checkInAppointmentId);
      setCheckInEligibility(eligibility);
      alert('PIN requested. The client will see it in the app. Ask them for the PIN and enter it below.');
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Failed to request PIN.');
    } finally {
      setCheckInLoading(false);
    }
  }, [checkInAppointmentId]);

  const handleVerifyCheckInPin = useCallback(async () => {
    if (!checkInAppointmentId || !checkInPinInput.trim()) {
      alert('Enter the 6-digit PIN from the client.');
      return;
    }
    setCheckInLoading(true);
    try {
      await appointmentService.verifyCheckInPin(checkInAppointmentId, checkInPinInput.trim());
      setCheckInModalOpen(false);
      setCheckInAppointmentId(null);
      setCheckInEligibility(null);
      setCheckInPinInput('');
      await dispatch(fetchAppointments());
      alert('Client checked in successfully.');
    } catch (err: any) {
      alert(err?.response?.data?.message || 'Invalid or expired PIN. Request a new one.');
    } finally {
      setCheckInLoading(false);
    }
  }, [checkInAppointmentId, checkInPinInput, dispatch]);

  const closeCheckInModal = useCallback(() => {
    setCheckInModalOpen(false);
    setCheckInAppointmentId(null);
    setCheckInEligibility(null);
    setCheckInPinInput('');
  }, []);

  const handleClick = useCallback((appointment: Appointment) => navigate(`/stylist/appointment/${publicAppointmentRef(appointment)}`), [navigate]);

  const getStatusColor = useCallback((st: string) => {
    switch (st) {
      case 'confirmed': return '#34C759';
      case 'pending': return '#FF9500';
      case 'completed': return '#C9A84C';
      case 'cancelled':
      case 'expired': return '#C0392B';
      case 'no-show': return '#C0392B';
      case 'in-progress':
      case 'checked-in': return '#C9A84C';
      case 'rescheduled': return '#8E8E93';
      default: return '#666';
    }
  }, []);

  const filtered = useMemo(() => {
    const currentBucket = APPOINTMENT_BUCKETS[tabValue];
    return appointments
      .filter(a => a.dateTime && a.status)
      .filter(a => {
        const s = a.status;
        return getAppointmentBucket(s, getAppointmentEndTime(a)) === currentBucket;
      })
      .filter(a => {
        const q = searchQuery.trim().toLowerCase();
        const clientName = a.client?.name || unknownClientName;
        const serviceName = a.service?.name || 'Service';
        return !q || clientName.toLowerCase().includes(q) || serviceName.toLowerCase().includes(q);
      })
      .sort((a, b) => {
        const diff = new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime();
        return getSortDirection(currentBucket) === 'asc' ? diff : -diff;
      });
  }, [appointments, tabValue, searchQuery]);

  const renderCard = useCallback((apt: Appointment) => {
    const id = apt._id!;
    const { date, time } = formatAppointmentDateTime(apt.dateTime);
    const statusStr = apt.status;
    const clientName = apt.client?.name || unknownClientName;
    const clientFirstName = clientName.split(' ')[0] || unknownClientName;
    const serviceName = apt.service?.name || 'Service';

    // Pricing logic with add-ons
    const orig = typeof apt.service?.originalPrice === 'number'
      ? apt.service.originalPrice
      : (typeof apt.service?.finalPrice === 'number' ? apt.service.finalPrice : 0);
    const fin = typeof apt.service?.finalPrice === 'number' ? apt.service.finalPrice : orig;
    const discount = orig - fin;
    
    // Calculate add-ons total with null safety
    const addOnsTotal = (apt.addons || []).reduce(
      (sum, addon) => sum + (typeof addon.priceCents === 'number' ? addon.priceCents / 100 : 0), 
      0
    );
    const hasAddOns = (apt.addons || []).length > 0;
    const totalPrice = fin + addOnsTotal;
    
    const isPast = isBefore(new Date(apt.dateTime), new Date());

    return (
      <div key={id} className="appointments__card" onClick={() => handleClick(apt)}>
        <div className="appointments__card-inner">
          {/* Avatar */}
          <div
            className="appointments__card-avatar"
            style={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundImage: `url(${apt.client?.profilePhoto || avatarPlaceholder})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />

          <div className="appointments__card-content">
            {/* Header */}
            <div className="appointments__card-header">
              <h3>{serviceName}</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexDirection: 'column' }}>
                {/* Unread message indicator (mail icon) - matching mobile implementation */}
                {unreadCounts[apt._id || ''] > 0 && (
                  <div className="appointments__unread-badge">
                    <EmailIcon size={18} color="#FFF" />
                    <span className="appointments__unread-count">{unreadCounts[apt._id || '']}</span>
                  </div>
                )}
                <span className="appointments__card-status" style={{ backgroundColor: getStatusColor(statusStr) }}>
                  {statusStr.charAt(0).toUpperCase() + statusStr.slice(1)}
                </span>
              </div>
            </div>

            {/* Details */}
            <div className="appointments__card-details">
              <div><CalendarIcon size={20} /> {date}</div>
              <div><TimeIcon size={20} /> {time}</div>
              <div className="appointments__card-client" title={clientName} style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }}>
                <PersonIcon size={20} /> {clientFirstName}
              </div>
            </div>

            {/* Discount badges */}
            {apt.discountSource && apt.discountSource !== 'none' && (
              <div style={{ marginTop: '4px', marginBottom: '4px' }}>
                <DiscountBadge
                  discountSource={apt.discountSource}
                  promotionApplied={apt.promotionApplied}
                  originalPrice={apt.service?.originalPrice}
                  finalPrice={apt.service?.finalPrice}
                  size="sm"
                />
              </div>
            )}

            {/* Footer: Price & check-in (decline/cancel live on Appointment Details) */}
            <div className="appointments__card-footer">
              <div className="appointments__card-price">
                {hasAddOns ? (
                  <div className="appointments__card-price-breakdown">
                    <div className="price-row">
                      <span className="label">Service:</span>
                      <span className="value">{formatCurrency(fin)}</span>
                    </div>
                    <div className="price-row">
                      <span className="label">Add-ons ({(apt.addons || []).length}):</span>
                      <span className="value">{formatCurrency(addOnsTotal)}</span>
                    </div>
                    <div className="price-row total">
                      <span className="label">Total:</span>
                      <span className="value">{formatCurrency(totalPrice)}</span>
                    </div>
                  </div>
                ) : discount > 0 ? (
                  <>
                    <span className="original">{formatCurrency(orig)}</span>
                    <span className="final">{formatCurrency(fin)}</span>
                  </>
                ) : (
                  <span>{formatCurrency(fin)}</span>
                )}
              </div>
              {!isPast && statusStr === 'confirmed' && (
                <div className="appointments__card-actions">
                  <button
                    onClick={e => { e.stopPropagation(); handleCheckIn(id); }}
                    disabled={checkInLoading}
                  >
                    {checkInLoading ? '…' : 'Check In'}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }, [getStatusColor, handleCheckIn, handleClick, unreadCounts, checkInLoading]);

  return (
    <div className="appointments">
      <header className="appointments__header">
        <h1>Your Appointments</h1>
        <button className="appointments__refresh" onClick={handleRefresh} disabled={refreshing}>
          <RefreshIcon size={24} />
        </button>
      </header>

      <div className="appointments__search">
        <input
          type="text"
          placeholder="Search by client or service"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="appointments__tabs">
        {['Pending','Confirmed','Past'].map((label, idx) => (
          <button
            key={label}
            className={`appointments__tab ${tabValue === idx ? 'appointments__tab--active' : ''}`}
            onClick={() => setTabValue(idx)}
          >
            {label}
          </button>
        ))}
      </div>

      <TabPanel value={tabValue} index={tabValue}>
        {isLoading || refreshing ? (
          <div className="appointments__loading"><AlertIcon size={20} /> Loading…</div>
        ) : filtered.length > 0 ? (
          <div className="appointments__list">{filtered.map(apt => renderCard(apt))}</div>
        ) : (
          <div className="appointments__empty"><AlertIcon size={20} /> No {APPOINTMENT_BUCKETS[tabValue]} appointments.</div>
        )}
      </TabPanel>

      {error && (
        <div className="appointments__error">
          <AlertIcon size={20} />
          <p>{error}</p>
        </div>
      )}

      {/* Check-in PIN modal (when PIN required or window passed) */}
      {checkInModalOpen && (
        <div className="appointments__modal-overlay" onClick={closeCheckInModal}>
          <div className="appointments__modal" onClick={e => e.stopPropagation()}>
            <h3>Check In with PIN</h3>
            <p className="appointments__modal-text">
              {checkInEligibility?.reason === 'TOO_LATE'
                ? 'Check-in window has passed. Request a PIN from the client to check in anyway. Ask them for the 6-digit code and enter it here.'
                : 'Check-in in this time window requires a PIN from the client. Request a PIN below, then ask the client for the 6-digit code and enter it here.'}
            </p>
            <div className="appointments__modal-actions">
              <button
                type="button"
                className="appointments__btn appointments__btn--primary"
                onClick={handleRequestCheckInPin}
                disabled={checkInLoading}
              >
                {checkInLoading ? '…' : 'Request PIN'}
              </button>
              <div className="appointments__modal-pin-row">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="Enter PIN from client"
                  value={checkInPinInput}
                  onChange={e => setCheckInPinInput(e.target.value.replace(/\D/g, ''))}
                  className="appointments__input"
                />
                <button
                  type="button"
                  className="appointments__btn appointments__btn--primary"
                  onClick={handleVerifyCheckInPin}
                  disabled={checkInLoading || !checkInPinInput.trim()}
                >
                  {checkInLoading ? '…' : 'Verify & Check In'}
                </button>
              </div>
              <button type="button" className="appointments__btn appointments__btn--secondary" onClick={closeCheckInModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StylistAppointmentsPage;
